/**
 * CafePod, 22 Sep 2026 (CP-073). Pack switching on the product card.
 *
 * Pod pack sizes are separate products (Brunch Blend, Brunch Blend 36 Pod Pack),
 * so the pack chips on a card are links to sibling products. Liquid renders them
 * as plain links and they work with JavaScript off. This module upgrades a click
 * on a chip into an in-place swap of the card: image, price, unit price,
 * subscription line, the variant the Add button will add, and every link on the
 * card, so a shopper can pick 10 / 36 / 40 and add without leaving the grid
 * (Jason, 22 Sep review).
 *
 * The data comes from data-swap-* attributes that snippets/cp-pack-chips.liquid
 * computes server-side. Nothing is fetched. Modifier clicks (new tab) and
 * middle clicks are left alone. Click is handled in the capture phase, because
 * a storefront script owns bubbling-phase clicks on some cards.
 *
 * CP-110 (27 Sep): the struck one-time price and "(Save 25%)" swap too, and hide
 * when the chosen pack carries no promo.
 */
// CP-106: size chips on bundle cards are buttons (variants of one product), pod pack chips are links.
const CHIP = 'a.cp-packs__chip[data-swap-url], button.cp-packs__chip[data-swap-url]';
const base = (u) => String(u || '').split('?')[0];

function cardRootFor(chip) {
  return chip.closest('product-card') || chip.closest('.cp-bg__card') || chip.closest('[data-cp-item]');
}

function swap(root, d) {
  const card = root.querySelector('.cp-card') || root;
  const fromUrl = card.dataset.cpUrl || '';

  // Links: title, overlay, chips' own siblings are excluded (they point elsewhere on purpose).
  if (fromUrl) {
    root.querySelectorAll('a[href]').forEach((a) => {
      if (a.matches(CHIP)) return;
      const href = a.getAttribute('href') || '';
      if (base(href) === base(fromUrl) && /^\/[^/]/.test(d.swapUrl || '')) a.setAttribute('href', d.swapUrl);
    });
  }

  // Image: Horizon's card gallery, or the brew-guide card media.
  const img = root.querySelector('.card-gallery img, .cp-bg__card-media img');
  if (img && d.swapImage && /^(https:\/\/cdn\.shopify\.com\/|\/\/cdn\.shopify\.com\/|\/cdn\/)/.test(d.swapImage)) {
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.src = d.swapImage;
    if (d.swapTitle) img.alt = d.swapTitle;
  }

  const set = (sel, text) => {
    const el = card.querySelector(sel);
    if (!el) return;
    if (text) {
      el.textContent = text;
      el.hidden = false;
    } else {
      el.hidden = true;
    }
  };
  set('.cp-card__amount', d.swapPrice);
  set('.cp-card__was', d.swapWas);
  set('.cp-card__save', d.swapSave);
  set('.cp-card__unit', d.swapUnit);
  set('.cp-card__sub', d.swapSub);

  // Every add form on the card, including Horizon's hidden quick-add one, so
  // nothing on the card can add the old pack.
  if (d.swapVariant) root.querySelectorAll('form input[name="id"]').forEach((i) => (i.value = d.swapVariant));
  root.querySelectorAll('product-form-component').forEach((c) => {
    if (d.swapProduct) c.dataset.productId = d.swapProduct;
  });

  Object.assign(card.dataset, {
    cpUrl: d.swapUrl || fromUrl,
    variantId: d.swapVariant || card.dataset.variantId,
    productId: d.swapProduct || card.dataset.productId,
    itemName: d.swapTitle || card.dataset.itemName,
    price: d.swapPriceMajor || card.dataset.price,
    itemId: d.swapSku || card.dataset.itemId,
  });
  if (root.tagName === 'PRODUCT-CARD' && d.swapProduct) root.dataset.productId = d.swapProduct;

  card.querySelectorAll(CHIP).forEach((c) => {
    const on = c.dataset.swapUrl === d.swapUrl;
    c.classList.toggle('is-current', on);
    if (c.tagName === 'BUTTON') c.setAttribute('aria-pressed', on ? 'true' : 'false');
    else if (on) c.setAttribute('aria-current', 'page');
    else c.removeAttribute('aria-current');
  });

  document.dispatchEvent(
    new CustomEvent('cp:pack-switched', { bubbles: true, detail: { url: d.swapUrl, pods: d.swapPods, productId: d.swapProduct } })
  );
}

/* CP-106: the size row scrolls sideways; fade its right edge only while more is hidden. */
function markScroll(list) {
  const over = list.scrollWidth > list.clientWidth + 1;
  list.toggleAttribute('data-overflow', over);
  list.toggleAttribute('data-at-end', !over || list.scrollLeft + list.clientWidth >= list.scrollWidth - 2);
}
function markAll() {
  document.querySelectorAll('[data-cp-size-scroll]').forEach(markScroll);
}
document.addEventListener(
  'scroll',
  (e) => {
    if (e.target instanceof Element && e.target.matches('[data-cp-size-scroll]')) markScroll(e.target);
  },
  { capture: true, passive: true }
);
window.addEventListener('resize', markAll, { passive: true });
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', markAll, { once: true });
else markAll();
document.addEventListener('shopify:section:load', markAll);

document.addEventListener(
  'click',
  (e) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const chip = e.target.closest(CHIP);
    if (!chip || !chip.closest('.cp-packs--compact')) return;
    const root = cardRootFor(chip);
    if (!root) return;
    e.preventDefault();
    e.stopPropagation();
    if (chip.classList.contains('is-current')) return;
    swap(root, chip.dataset);
  },
  true
);
